/**
 * arrocha-timeouts.js
 * Farmacias Arrocha — Módulo 4: Controladores de timeout para peticiones de red
 *
 * Propósito:
 *   Interceptar todas las peticiones HTTP (fetch y XMLHttpRequest) que realiza
 *   la tienda y cancelarlas si exceden el tiempo máximo permitido (TIMEOUT_MS).
 *   Cuando una petición se cancela por timeout, se muestra un toast informativo
 *   al cliente indicando que la conexión está tardando más de lo normal.
 *
 * Problema que resuelve:
 *   En conexiones lentas o inestables, las peticiones HTTP pueden quedar
 *   pendientes indefinidamente, bloqueando la interfaz del usuario sin ningún
 *   mensaje de retroalimentación. Este script establece un límite de tiempo
 *   máximo y notifica al usuario de forma amigable cuando se supera.
 *
 * Estrategia de implementación:
 *   - Para fetch: se usa AbortController para cancelar la petición y se
 *     envuelve window.fetch con un wrapper que inyecta el timeout.
 *   - Para XMLHttpRequest: se envuelven los métodos open() y send() del
 *     prototype para evitar "Illegal invocation" y se llama a xhr.abort()
 *     cuando se supera el timeout.
 *   - Las peticiones de analytics, tracking y checkout de Shopify se excluyen
 *     para evitar interferir con procesos críticos del sistema.
 *
 * Diseño del toast (aprobado por el cliente — Mayo 2026):
 *   - Fondo blanco (#ffffff) con borde sutil #e8e8e8
 *   - Layout en CSS Grid: icono centrado al párrafo, título arriba, botón abajo a la derecha
 *   - Emoji Noto 😥 (1f625) vía img desde fonts.gstatic.com — consistente en todos los navegadores
 *   - Título en negro negrita: "La conexión está tardando más de lo normal"
 *   - Mensaje en gris: descripción amigable con sugerencia de reintento
 *   - Botón rojo corporativo Arrocha (#D32630) alineado a la derecha, ancho fit-content
 *   - Sin botón de cierre (X) — se oculta automáticamente después de 10s
 *   - Sin borde lateral de color
 *   - Bordes redondeados (16px)
 *   - Centrado horizontalmente en la parte inferior de la pantalla
 *   - Responsive: en mobile ajusta ancho del texto con variable CSS
 *
 * Dependencias: Ninguna (vanilla JS, compatible con todos los navegadores modernos)
 *
 * Autor: Informatica EasyGo
 * Versión: 2.2.0
 * Fecha: Mayo 2026
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════
     CONFIGURACIÓN
     ═══════════════════════════════════════════════════════════════════════ */

  /** Tiempo máximo en milisegundos antes de cancelar una petición HTTP */
  var TIMEOUT_MS = 12000; // 12 segundos — estándar producción

  /** Tiempo en milisegundos que el toast permanece visible antes de ocultarse */
  var TOAST_DISPLAY_MS = 10000; // 10 segundos

  /** Tiempo mínimo entre apariciones del toast (anti-spam) */
  var TOAST_COOLDOWN_MS = 15000; // 15 segundos

  /* ═══════════════════════════════════════════════════════════════════════
     EXCLUSIONES DE URL
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Lista de patrones de URL que NO deben ser interceptados.
   * Incluye servicios de analytics, tracking, checkout nativo de Shopify
   * y otros endpoints críticos que no deben ser cancelados por timeout.
   */
var EXCLUDED_PATTERNS = [
    'googletagmanager.com',
    'google-analytics.com',
    'analytics',
    'beacon',
    'shopify.com/checkouts',
    '/checkouts/',
    'pay.shopify.com',
    'v.shopify.com',
    'shopifysvc.com',
    'otlp-http-production',
    '/api/collect',
    'login_with_shop',
    'b5bfe654',
    'trekkie',
    'monorail',
    'perf-kit',
    // === Agregados v2.3.0 — Mayo 2026 ===
    // Apps de terceros que realizan llamadas en segundo plano y pueden
    // exceder el umbral de timeout por condiciones de su propia infraestructura
    'easylockdownapp.com',  // Easylockdown (control de acceso)
    'globo.io',             // Globo Form Builder
    'attentivemobile.com',  // Attentive (marketing)
    'attentive.com',        // Attentive (marketing - dominio alterno)
    'cdn.shopify.com',      // CDN de Shopify (assets, imágenes)
    'shopifycdn.com',       // CDN de Shopify (fuentes)
    'web-pixel',            // Web Pixels de Shopify
    'sentry.io',            // Sentry error tracking
    'starapps',             // StarApps Core
    'okendo',               // Okendo Reviews
    'shogun',               // Shogun page builder
    'boost',                // Boost PFS
    'bsscommerce',          // BSS Commerce
    'treedify',             // Treedify / Selly
    'slapp',                // Selly
  ];

  /**
   * isExcluded
   *
   * Verifica si una URL debe ser excluida del control de timeout.
   * Recorre la lista EXCLUDED_PATTERNS y retorna true si la URL
   * contiene alguno de los patrones definidos.
   *
   * @param {string} url - URL a verificar
   * @returns {boolean} true si la URL debe ser excluida, false si debe ser interceptada
   */
  function isExcluded(url) {
    if (!url) return false;
    var urlStr = String(url);
    for (var i = 0; i < EXCLUDED_PATTERNS.length; i++) {
      if (urlStr.indexOf(EXCLUDED_PATTERNS[i]) !== -1) return true;
    }
    return false;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     TOAST DE NOTIFICACIÓN
     Diseño aprobado por el cliente (Mayo 2026):
     Fondo blanco, emoji 😓 grande (~80px) a la izquierda alineado al centro
     del bloque, título negro negrita, botón rojo #D32630 ancho completo,
     sin botón de cierre, sin borde lateral, bordes redondeados 16px.
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * injectToastStyles
   *
   * Inyecta dinámicamente los estilos CSS del toast de notificación en el <head>.
   * Se ejecuta una sola vez — si los estilos ya existen no vuelve a inyectarlos.
   *
   * Especificaciones de diseño (según mockup aprobado):
   *   - Fondo: #ffffff (blanco)
   *   - Emoji: ~80px, flex-shrink 0, centrado verticalmente con el bloque de texto
   *   - Texto título: #1a1a1a (negro), font-weight 700, font-size 16px
   *   - Texto mensaje: #555555 (gris medio), font-size 13px, line-height 1.5
   *   - Botón: fondo #D32630 (rojo corporativo Arrocha), texto blanco,
   *            border-radius 8px, ancho 100% del bloque de texto
   *   - Botón hover: fondo #b81f28 (rojo más oscuro)
   *   - Sombra: 0 4px 24px rgba(0,0,0,0.13)
   *   - Border-radius: 16px
   *   - Animación: slide-up con bounce (cubic-bezier)
   *   - Max-width: 480px, centrado horizontalmente
   *   - Gap entre emoji y bloque: 20px
   */
  function injectToastStyles() {
    if (document.getElementById('arrocha-timeout-styles')) return;
    var style = document.createElement('style');
    style.id = 'arrocha-timeout-styles';
    style.textContent = [
      '#arrocha-timeout-toast {',
      '  position: fixed;',
      '  bottom: 32px;',
      '  left: 50%;',
      '  transform: translateX(-50%) translateY(140px);',
      '  background: #fff;',
      '  color: #1a1a1a;',
      '  padding: 14px 20px;',
      '  border-radius: 16px;',
      '  font-family: inherit;',
      '  box-shadow: 0 2px 16px rgba(0,0,0,0.12);',
      '  z-index: 99999;',
      '  --arrocha-icon-width: 60px;',
      '  --arrocha-gap: 16px;',
      '  --arrocha-text-width: 365px;',
      '  width: fit-content;',
      '  max-width: calc(100vw - 24px);',
      '  display: grid;',
      '  grid-template-columns: var(--arrocha-icon-width) var(--arrocha-text-width);',
      '  grid-template-areas: ". title" "icon message" ". actions";',
      '  column-gap: var(--arrocha-gap);',
      '  border: 1px solid #e8e8e8;',
      '  box-sizing: border-box;',
      '  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease;',
      '  opacity: 0;',
      '  pointer-events: none;',
      '}',
      '#arrocha-timeout-toast * {',
      '  box-sizing: border-box;',
      '}',
      '#arrocha-timeout-toast.arrocha-toast--visible {',
      '  transform: translateX(-50%) translateY(0);',
      '  opacity: 1;',
      '  pointer-events: auto;',
      '}',
      '#arrocha-timeout-toast .arrocha-toast__title {',
      '  grid-area: title;',
      '  font-weight: 700;',
      '  font-size: 17px;',
      '  color: #1a1a1a;',
      '  margin: 0 0 8px 0;',
      '  line-height: 1.3;',
      '}',
      '#arrocha-timeout-toast .arrocha-toast__icon {',
      '  grid-area: icon;',
      '  align-self: center;',
      '  width: 60px;',
      '  height: 60px;',
      '}',
      '#arrocha-timeout-toast .arrocha-toast__icon img {',
      '  width: 60px;',
      '  height: 60px;',
      '  display: block;',
      '}',
      '#arrocha-timeout-toast .arrocha-toast__message {',
      '  grid-area: message;',
      '  font-size: 13px;',
      '  color: #444;',
      '  margin: 0;',
      '  line-height: 1.5;',
      '  overflow-wrap: normal;',
      '  word-break: normal;',
      '}',
      '#arrocha-timeout-toast .arrocha-toast__actions {',
      '  grid-area: actions;',
      '  width: 100%;',
      '  display: flex;',
      '  justify-content: flex-end;',
      '  margin-top: 10px;',
      '}',
      '#arrocha-timeout-toast .arrocha-toast__btn {',
      '  background: #D32630;',
      '  color: #fff;',
      '  border: none;',
      '  border-radius: 8px;',
      '  padding: 10px 24px;',
      '  font-size: 14px;',
      '  font-weight: 600;',
      '  cursor: pointer;',
      '  font-family: inherit;',
      '  white-space: nowrap;',
      '  transition: background 0.2s ease;',
      '}',
      '#arrocha-timeout-toast .arrocha-toast__btn:hover {',
      '  background: #b81f28;',
      '}',
      '@media (max-width: 480px) {',
      '  #arrocha-timeout-toast {',
      '    --arrocha-text-width: calc(100vw - 124px);',
      '    padding: 14px 16px;',
      '  }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  /** Timer para ocultar el toast automáticamente después de mostrarlo */
  var toastTimer = null;

  /**
   * Flag anti-spam del toast.
   * Evita que el toast se muestre múltiples veces en un período corto
   * cuando varias peticiones fallan simultáneamente por timeout.
   * Se resetea automáticamente después de TOAST_COOLDOWN_MS milisegundos.
   */
  var toastShownRecently = false;

  /**
   * showTimeoutToast
   *
   * Crea y muestra el toast de notificación de timeout al usuario.
   * Si el toast ya está visible o se mostró recientemente, no hace nada
   * (protección anti-spam).
   *
   * El toast incluye:
   *   - Emoji de carita triste con sudor (😓) grande (~80px) a la izquierda
   *   - Título: "La conexión esta tardando más de lo normal"
   *   - Mensaje: Explicación amigable con sugerencia de reintento
   *   - Botón "Intentar de nuevo" ancho completo que recarga la página
   *
   * Se oculta automáticamente después de TOAST_DISPLAY_MS milisegundos.
   * No tiene botón de cierre manual — se oculta solo por timeout.
   */
  function showTimeoutToast() {
    if (toastShownRecently) return;
    toastShownRecently = true;
    setTimeout(function () { toastShownRecently = false; }, TOAST_COOLDOWN_MS);

    injectToastStyles();

    var toast = document.getElementById('arrocha-timeout-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'arrocha-timeout-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');

      toast.innerHTML = [
        '<p class="arrocha-toast__title">La conexi\u00f3n est\u00e1 tardando m\u00e1s de lo normal</p>',
        '<div class="arrocha-toast__icon">',
        '  <img src="https://fonts.gstatic.com/s/e/notoemoji/latest/1f625/512.png" alt="">',
        '</div>',
        '<p class="arrocha-toast__message">Tu conexi\u00f3n parece estar lenta en este momento o el sistema est\u00e1 experimentando demoras. Por favor, intenta nuevamente en unos minutos o presiona el bot\u00f3n para reintentar.</p>',
        '<div class="arrocha-toast__actions">',
        '  <button class="arrocha-toast__btn" onclick="window.location.reload()">Intentar de nuevo</button>',
        '</div>',
      ].join('');

      document.body.appendChild(toast);
    }

    clearTimeout(toastTimer);
    requestAnimationFrame(function () {
      toast.classList.add('arrocha-toast--visible');
    });

    toastTimer = setTimeout(function () {
      toast.classList.remove('arrocha-toast--visible');
    }, TOAST_DISPLAY_MS);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     INTERCEPTOR DE FETCH
     Reemplaza window.fetch con un wrapper que inyecta timeout via
     AbortController. Las peticiones excluidas pasan directamente al
     fetch original sin modificación.
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Wrapper de window.fetch
   *
   * Reemplaza la función nativa window.fetch con una versión que inyecta
   * un timeout via AbortController. Si la petición no responde dentro de
   * TIMEOUT_MS milisegundos, se aborta y se muestra el toast de notificación.
   *
   * Maneja correctamente:
   *   - URLs excluidas (las pasa directamente al fetch original)
   *   - Señales de abort existentes (las encadena con la señal del timeout)
   *   - Limpieza del timer cuando la petición responde a tiempo
   */
  var originalFetch = window.fetch;

  window.fetch = function (resource, options) {
    var url = (resource && resource.url) ? resource.url : String(resource);

    if (isExcluded(url)) {
      return originalFetch.apply(this, arguments);
    }

    var controller = new AbortController();
    var existingSignal = options && options.signal;
    var mergedOptions = Object.assign({}, options, { signal: controller.signal });

    // Si ya existe una señal de abort, encadenarla con la del timeout
    if (existingSignal) {
      existingSignal.addEventListener('abort', function () {
        controller.abort();
      });
    }

    var timeoutId = setTimeout(function () {
      controller.abort();
      showTimeoutToast();
      console.warn('[Arrocha Timeouts] fetch abortado por timeout:', url);
    }, TIMEOUT_MS);

    return originalFetch.call(window, resource, mergedOptions).then(
      function (response) {
        clearTimeout(timeoutId);
        return response;
      },
      function (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    );
  };

  /* ═══════════════════════════════════════════════════════════════════════
     INTERCEPTOR DE XMLHTTPREQUEST
     Envuelve los métodos open() y send() del prototype para inyectar
     timeout sin causar "Illegal invocation". Las peticiones excluidas
     pasan directamente sin modificación.
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Wrapper de XMLHttpRequest
   *
   * Envuelve los métodos open() y send() del prototype de XMLHttpRequest
   * para inyectar el timeout sin causar "Illegal invocation".
   *
   * - open(): almacena la URL de la petición en _arrochUrl para uso posterior
   * - send(): configura el timer de timeout y lo cancela cuando la petición
   *   finaliza (éxito, error o abort) via el evento 'loadend'
   */
  var OriginalXHR = window.XMLHttpRequest;
  var originalOpen = OriginalXHR.prototype.open;
  var originalSend = OriginalXHR.prototype.send;

  /**
   * XMLHttpRequest.prototype.open (wrapper)
   * Guarda la URL en _arrochUrl para poder evaluarla en send().
   */
  OriginalXHR.prototype.open = function (method, url) {
    this._arrochUrl = url;
    return originalOpen.apply(this, arguments);
  };

  /**
   * XMLHttpRequest.prototype.send (wrapper)
   * Configura el timer de timeout para peticiones no excluidas.
   * Usa el evento 'loadend' para limpiar el timer cuando la petición finaliza.
   */
  OriginalXHR.prototype.send = function () {
    var xhr = this;
    var url = xhr._arrochUrl || '';

    if (!isExcluded(url)) {
      var timeoutId = setTimeout(function () {
        try {
          xhr.abort();
          showTimeoutToast();
          console.warn('[Arrocha Timeouts] XHR abortado por timeout:', url);
        } catch (e) {}
      }, TIMEOUT_MS);

      xhr.addEventListener('loadend', function () {
        clearTimeout(timeoutId);
      });
    }

    return originalSend.apply(this, arguments);
  };

  /* ═══════════════════════════════════════════════════════════════════════
     INICIALIZACIÓN
     ═══════════════════════════════════════════════════════════════════════ */

  console.log('[Arrocha Timeouts] Controladores activos \u2013 timeout:', TIMEOUT_MS + 'ms');

})();